import { useToast } from '@televet/kibble-ui';
import { useCallback } from 'react';
import { GraphQLErrorPolicies, GraphQLFetchPolicies } from 'shared/enums';
import { useIntegrationsProvider } from 'shared/providers/IntegrationsProvider';
import { useChannelExportToPimsMutation, useGetChannelPdfDownloadDataLazyQuery } from 'shared/types/graphql';
import { useAppDispatch, useAppSelector } from 'state/hooks';
import { resetExportData } from '../state/conversationsSlice';
import { downloadPdf } from '../utils/files';

interface UseExportOrDownloadChannelReturnValue {
  exportChannelToPims: () => void;
  downloadChannelPdf: () => void;
  isLoading: boolean;
}

interface UseExportOrDownloadChannelProps {
  channelId: string;
  shouldIncludeNotes: boolean;
}

export const useExportOrDownloadChannel = ({
  channelId,
  shouldIncludeNotes,
}: UseExportOrDownloadChannelProps): UseExportOrDownloadChannelReturnValue => {
  const toast = useToast();
  const { primaryIntegrationName } = useIntegrationsProvider();
  const { exportFileName } = useAppSelector((state) => state.conversations.exportData);
  const dispatch = useAppDispatch();

  const [exportChannelToPims, { loading: isExportingChannelToPims }] = useChannelExportToPimsMutation({
    errorPolicy: GraphQLErrorPolicies.All,
    variables: {
      where: { id: channelId, includeNotes: shouldIncludeNotes, fileName: exportFileName },
    },
    onCompleted: (data) => {
      const { channelExportToPims } = data;
      dispatch(resetExportData());

      if (channelExportToPims?.success) {
        toast({ title: 'Success!', description: `Conversation successfully exported to ${primaryIntegrationName}` });
      } else {
        toast({
          status: 'error',
          title: `Conversation failed to export to ${primaryIntegrationName}`,
          description: channelExportToPims?.payload?.status,
        });
      }
    },
  });

  const [getChannelPDFDownloadData, { loading: isFetchingDownloadData }] = useGetChannelPdfDownloadDataLazyQuery();

  const downloadChannelPdf = useCallback(async () => {
    await getChannelPDFDownloadData({
      fetchPolicy: GraphQLFetchPolicies.NoCache,
      variables: {
        where: { id: channelId, includeNotes: shouldIncludeNotes, fileName: exportFileName },
      },
      onCompleted: (data) => {
        downloadPdf(`data:application/pdf;base64,${data?.channelExport?.base64Encoding}`, `${exportFileName}.pdf`);
        toast({ title: 'Success', description: 'Conversation successfully downloaded.' });
        dispatch(resetExportData());
      },
      onError: () => {
        toast({ title: 'Error generating PDF', description: 'Please try again or contact support.', status: 'error' });
      },
    });
  }, [getChannelPDFDownloadData, channelId, shouldIncludeNotes, exportFileName, dispatch, toast]);

  return {
    exportChannelToPims,
    downloadChannelPdf,
    isLoading: isExportingChannelToPims || isFetchingDownloadData,
  };
};
